import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const defaultWeeklyHours = {
  mon: { open: "09:00", close: "17:00" },
  tue: { open: "09:00", close: "17:00" },
  wed: { open: "09:00", close: "17:00" },
  thu: { open: "09:00", close: "17:00" },
  fri: { open: "09:00", close: "17:00" },
  sat: null,
  sun: null,
};

const industries = [
  { value: "dental_clinic", label: "Dental Clinic" },
  { value: "med_spa", label: "Med Spa" },
  { value: "chiropractor", label: "Chiropractor" },
  { value: "auto_repair", label: "Auto Repair" },
  { value: "salon", label: "Salon / Beauty" },
  { value: "veterinary", label: "Veterinary Clinic" },
];

export function OnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  const [name, setName] = useState("");
  const [industry, setIndustry] = useState("dental_clinic");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [calendarEmail, setCalendarEmail] = useState("");

  const [services, setServices] = useState([
    { name: "Consultation", duration_min: 20 },
  ]);

  const [weeklyHours, setWeeklyHours] = useState(defaultWeeklyHours);
  const [timezone, setTimezone] = useState("America/New_York");

  const [faqs, setFaqs] = useState([]);
  const [extraContext, setExtraContext] = useState("");

  const [loading, setLoading] = useState(false);
  const [createdClientId, setCreatedClientId] = useState(null);
  const [createdApiKey, setCreatedApiKey] = useState(null);
  const [error, setError] = useState(null);

  const addService = () =>
    setServices((prev) => [...prev, { name: "", duration_min: 15 }]);

  const updateService = (index, field, value) => {
    setServices((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s))
    );
  };

  const removeService = (index) =>
    setServices((prev) => prev.filter((_, i) => i !== index));

  const addFaq = () =>
    setFaqs((prev) => [...prev, { question: "", answer: "" }]);

  const updateFaq = (index, field, value) => {
    setFaqs((prev) =>
      prev.map((f, i) => (i === index ? { ...f, [field]: value } : f))
    );
  };

  const removeFaq = (index) =>
    setFaqs((prev) => prev.filter((_, i) => i !== index));

  const toggleClosed = (day) => {
    setWeeklyHours((prev) => ({
      ...prev,
      [day]: prev[day] ? null : { open: "09:00", close: "17:00" },
    }));
  };

  const updateHours = (day, field, value) => {
    setWeeklyHours((prev) => ({
      ...prev,
      [day]: prev[day] ? { ...prev[day], [field]: value } : prev[day],
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = {
        name,
        industry,
        phone_number: phoneNumber,
        calendar_email: calendarEmail,
        business_hours: {
          timezone,
          weekly_hours: weeklyHours,
        },
        services,
        call_rules: {
          allow_new_patients: true,
          allow_emergencies: true,
          max_book_ahead_days: 30,
          buffer_minutes: 10,
        },
        faqs,
        extra_context: extraContext,
      };

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || "Failed to create client");
      }

      const data = await res.json();
      
      // Save to localStorage for dashboard access
      window.localStorage.setItem("clientId", data.id);
      window.localStorage.setItem("apiKey", data.api_key);
      
      setCreatedClientId(data.id);
      setCreatedApiKey(data.api_key);
      setStep(4);
    } catch (e) {
      setError(e.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const stepClasses = "flex flex-col gap-6";

  return (
    <div className="min-h-screen bg-slate-50 flex justify-center px-4 py-12 pt-24">
      <div className="w-full max-w-4xl bg-white border border-slate-200 rounded-2xl p-8 md:p-10 shadow-xl">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">
          Set up your AI Receptionist
        </h1>
        <p className="text-slate-600 mb-8 text-base">
          Three quick steps and Calyro will be ready to answer calls for your
          business.
        </p>

        {/* Step indicator */}
        <div className="flex items-center gap-4 mb-10 text-sm">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-md ${
                  step === s
                    ? "bg-gradient-to-r from-sky-500 to-blue-500 text-white"
                    : step > s
                    ? "bg-emerald-500 text-white"
                    : "bg-slate-200 text-slate-500"
                }`}
              >
                {step > s ? "✓" : s}
              </div>
              <span
                className={`font-semibold ${
                  step === s
                    ? "text-slate-900"
                    : step > s
                    ? "text-emerald-600"
                    : "text-slate-400"
                }`}
              >
                {s === 1 && "Business"}
                {s === 2 && "Services"}
                {s === 3 && "Hours & FAQs"}
              </span>
              {s !== 3 && (
                <div className={`w-12 h-[2px] mx-2 rounded-full ${
                  step > s ? "bg-emerald-300" : "bg-slate-200"
                }`} />
              )}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className={stepClasses}>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Business name</label>
              <input
                className="w-full bg-white border-2 border-slate-200 rounded-xl px-4 py-3 text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Bright Smile Dental"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Industry</label>
                <select
                  className="w-full bg-white border-2 border-slate-200 rounded-xl px-4 py-3 text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                >
                  {industries.map((i) => (
                    <option key={i.value} value={i.value}>
                      {i.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Business phone</label>
                <input
                  className="w-full bg-white border-2 border-slate-200 rounded-xl px-4 py-3 text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="+1 555-123-4567"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Calendar email (Google)
              </label>
              <input
                className="w-full bg-white border-2 border-slate-200 rounded-xl px-4 py-3 text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                value={calendarEmail}
                onChange={(e) => setCalendarEmail(e.target.value)}
                placeholder="reception@yourclinic.com"
              />
              <p className="text-xs text-slate-500 mt-1">
                We'll start by using this email for booking. Full Google
                Calendar OAuth can be added later.
              </p>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setStep(2)}
                disabled={!name || !phoneNumber || !calendarEmail}
                className="px-6 py-3 rounded-xl bg-gradient-to-r from-sky-500 to-blue-500 hover:from-sky-600 hover:to-blue-600 text-white text-base font-bold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Next: Services →
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className={stepClasses}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900">Services you offer</h2>
              <button
                onClick={addService}
                className="text-sm px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold transition-all"
              >
                + Add service
              </button>
            </div>

            <div className="space-y-3">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="grid grid-cols-12 gap-3 items-center bg-slate-50 border-2 border-slate-200 rounded-xl px-4 py-3"
                >
                  <input
                    className="col-span-7 bg-transparent text-sm focus:outline-none"
                    value={service.name}
                    onChange={(e) =>
                      updateService(index, "name", e.target.value)
                    }
                    placeholder="Cleaning / Consultation / Oil change"
                  />
                  <div className="col-span-3 flex items-center gap-1">
                    <input
                      type="number"
                      className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1 text-sm"
                      value={service.duration_min}
                      onChange={(e) =>
                        updateService(index, "duration_min", Number(e.target.value))
                      }
                    />
                    <span className="text-xs text-slate-500">min</span>
                  </div>
                  {services.length > 1 && (
                    <button
                      onClick={() => removeService(index)}
                      className="col-span-2 text-xs text-slate-600 hover:text-red-400 text-right"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-between gap-3 mt-4">
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm"
              >
                Back
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={services.some(s => !s.name || s.duration_min < 1)}
                className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-sm font-medium disabled:opacity-50"
              >
                Next: Hours & FAQs
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className={stepClasses}>
            <div>
              <label className="block text-sm mb-1">Timezone</label>
              <input
                className="w-full bg-gradient-to-br from-white via-blue-50 to-indigo-50 border border-slate-200 rounded-lg px-3 py-2 text-sm"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                placeholder="America/New_York"
              />
            </div>

            <div>
              <h2 className="text-sm font-medium mb-2">Weekly hours</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(weeklyHours).map(([day, value]) => (
                  <div
                    key={day}
                    className="flex items-center justify-between bg-gradient-to-br from-white via-blue-50 to-indigo-50 border border-slate-200 rounded-xl px-3 py-2 text-xs"
                  >
                    <span className="capitalize w-16">
                      {day.slice(0, 3)}
                    </span>
                    {value ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="time"
                          value={value.open}
                          onChange={(e) =>
                            updateHours(day, "open", e.target.value)
                          }
                          className="bg-white border border-slate-300 rounded px-2 py-1"
                        />
                        <span>–</span>
                        <input
                          type="time"
                          value={value.close}
                          onChange={(e) =>
                            updateHours(day, "close", e.target.value)
                          }
                          className="bg-white border border-slate-300 rounded px-2 py-1"
                        />
                        <button
                          onClick={() => toggleClosed(day)}
                          className="text-slate-600 hover:text-red-400"
                        >
                          Closed
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => toggleClosed(day)}
                        className="text-slate-600 hover:text-emerald-400"
                      >
                        Closed – set hours
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-medium">FAQs for the AI</h2>
                <button
                  onClick={addFaq}
                  className="text-xs px-3 py-1 rounded-full bg-slate-800 hover:bg-slate-700"
                >
                  + Add FAQ
                </button>
              </div>
              <div className="space-y-3">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-gradient-to-br from-white via-blue-50 to-indigo-50 border border-slate-200 rounded-xl px-4 py-3 text-xs flex flex-col gap-2"
                  >
                    <input
                      className="bg-transparent focus:outline-none text-slate-800"
                      placeholder="Question (e.g. Do you take Delta Dental?)"
                      value={faq.question}
                      onChange={(e) =>
                        updateFaq(index, "question", e.target.value)
                      }
                    />
                    <textarea
                      className="bg-white border border-slate-200 rounded-lg px-2 py-1 focus:outline-none text-slate-800"
                      rows={2}
                      placeholder="Short, clear answer"
                      value={faq.answer}
                      onChange={(e) =>
                        updateFaq(index, "answer", e.target.value)
                      }
                    />
                    <div className="text-right">
                      <button
                        onClick={() => removeFaq(index)}
                        className="text-slate-500 hover:text-red-400"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
                {faqs.length === 0 && (
                  <p className="text-xs text-slate-500">
                    Add 3–5 common questions so your AI can answer like your
                    best receptionist.
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm mb-1">
                Extra context (optional)
              </label>
              <textarea
                className="w-full bg-gradient-to-br from-white via-blue-50 to-indigo-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800"
                rows={3}
                value={extraContext}
                onChange={(e) => setExtraContext(e.target.value)}
                placeholder="Anything special the AI should know about your business, policies, VIP customers, etc."
              />
            </div>

            {error && (
              <div className="text-sm text-red-400 bg-red-950/40 border border-red-800 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <div className="flex justify-between gap-3 mt-4">
              <button
                onClick={() => setStep(2)}
                className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm"
              >
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-sm font-medium disabled:opacity-60"
              >
                {loading ? "Creating..." : "Create my AI receptionist"}
              </button>
            </div>
          </div>
        )}

        {step === 4 && createdClientId && createdApiKey && (
          <div className="mt-6">
            <div className="rounded-xl bg-emerald-950/40 border border-emerald-700 px-6 py-6 text-sm">
              <p className="font-medium text-emerald-300 mb-3 text-lg">
                🎉 You're all set!
              </p>
              <p className="text-slate-800 mb-2">
                Calyro.ai has created your business profile.
              </p>
              
              <div className="space-y-3 my-4">
                <div>
                  <p className="text-slate-600 text-xs mb-1">Client ID:</p>
                  <div className="font-mono text-xs bg-white px-3 py-2 rounded-lg border border-slate-300">
                    {createdClientId}
                  </div>
                </div>
                
                <div>
                  <p className="text-slate-600 text-xs mb-1">API Key:</p>
                  <div className="font-mono text-xs bg-white px-3 py-2 rounded-lg border border-slate-300 break-all">
                    {createdApiKey}
                  </div>
                </div>
              </div>
              
              <p className="text-slate-600 text-xs mb-6">
                ⚠️ <strong>Save these credentials securely!</strong> You'll need them to configure your Vapi agent. Your credentials have been saved to your browser for dashboard access.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => navigate("/dashboard")}
                  className="px-6 py-3 rounded-lg bg-sky-500 hover:bg-sky-600 text-sm font-medium"
                >
                  Go to Dashboard
                </button>
                <button
                  onClick={() => navigate("/")}
                  className="px-6 py-3 rounded-lg bg-slate-700 hover:bg-slate-600 text-sm font-medium"
                >
                  Back to Homepage
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
